/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2023] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';

import SelectRowCell from '@/backgrid/SelectRowCell';
import GridView from '@/views/GridView';
import StringCell from '@/backgrid/StringCell';
import { DateCell } from '@/backgrid/MomentCell';
import showErrorMessage from '@/util/showErrorMessage';
import generateCSV from '@/util/generateCSV';
import downloadCSV from '@/util/downloadCSV';
import PageableCollection from '@/backbone/PageableCollection';

const InventoryDashboardGridView = GridView.extend({

  ui: {
    grid: 'input',
  },

  events: {
    'change @ui.grid': 'onChangeGridCheckbox'
  },

  initialize() {
    const columns = [
      {
        name: 'asset',
        label: 'Asset',
        cell: StringCell,
        headerCell: 'custom',
        width: 5,
        editable: false,
        sortable: true
      },
      {
        name: EXTERNAL ? 'inventoryNumber' : 'serialNumber',
        label: 'Serial Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'inventoryNumber',
        label: 'Inventory Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true,
        renderable: !EXTERNAL
      },
      {
        name: 'location',
        label: 'Location',
        cell: StringCell,
        headerCell: 'custom',
        width: 30,
        editable: false,
        sortable: true
      },
      {
        name: 'subLocation',
        label: 'Sub Location',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        sortable: true
      },
      {
        name: 'facilityText',
        label: 'Command',
        cell: StringCell,
        headerCell: 'custom',
        width: 20,
        editable: false,
        sortable: true
      },
      {
        name: 'plant',
        label: 'Plant',
        cell: StringCell,
        headerCell: 'custom',
        width: 5,
        editable: false,
        sortable: true
      },
      {
        name: 'vendor',
        label: 'Vendor Cage Code',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'manufacturer',
        label: 'Manufacturer',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'orgAquisYear',
        label: 'Org Aquis Year',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'originalValue',
        label: 'Original Value',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true,
        renderable: !EXTERNAL
      },
      {
        name: 'make',
        label: 'Make',
        cell: StringCell,
        headerCell: 'custom',
        width: 5,
        editable: false,
        sortable: true
      },
      {
        name: 'model',
        label: 'Model',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'contractAwardDate',
        label: 'Contract Award Date',
        cell: DateCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'contractEndDate',
        label: 'Contract End Date',
        cell: DateCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'eqInstallDate',
        label: 'Eq Install Date',
        cell: DateCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'uninstallDate',
        label: 'Uninstall Date',
        cell: DateCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'piin',
        label: 'PIIN/SPIN',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true,
        renderable: !EXTERNAL
      },
      {
        name: 'clin',
        label: 'CLIN',
        cell: StringCell,
        headerCell: 'custom',
        width: 5,
        editable: false,
        sortable: true
      },
      {
        name: 'miprNumber',
        label: 'MIPR Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'material',
        label: 'Material',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'miprCode',
        label: 'MIPR Code',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'leaseTerm',
        label: 'Lease Term',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'name',
        label: 'POC Name',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'email',
        label: 'POC Email',
        cell: StringCell,
        headerCell: 'custom',
        width: 20,
        editable: false,
        sortable: true
      },
      {
        name: 'phone',
        label: 'POC Phone',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'sellingPrice',
        label: 'Selling Price',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'gInvoicing',
        label: 'G-Invoicing Order Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        sortable: true
      },
      {
        name: 'uniqueId',
        label: 'Unique ID',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      },
      {
        name: 'lineItemNumber',
        label: 'Line Item Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        sortable: true
      }
    ];

    this.options.columns = columns;
    this.options.columns, (this.collection = new PageableCollection([]));
    this.options.emptyText = 'No Inventory Found';
    this.options.width = '5600px';
    GridView.prototype.initialize?.apply(this, this.options);
  },

  async generateAndDownloadCSV() {
    try {
      const columnsToInclude = _.reduce(
        this.options.columns.toJSON(),
        (acc, column) => {
          if ((_.isUndefined(column.renderable) || column.renderable) && column.label) {
            acc.push({
              name: column.name,
              label: column.label
            });
          }
          return acc;
        },
        []
      );

      const data = this.options.collection.fullCollection.toJSON();
      const csvContent = await generateCSV(data, columnsToInclude);
      const fileName = `inventory`;
      await downloadCSV(csvContent, fileName);
    } catch (err) {
      showErrorMessage({
        errorCode: 'downloadError',
        errorTitle: 'Download Error',
        errorMessage: 'An error occurred while downloading CSV. Please try again or contact an administrator.'
      });
    }
  },

  async showSelectColumn() {
    this.options.columns.unshift({
      name: 'selected',
      label: '',
      ariaLabel: 'Select',
      cell: SelectRowCell.SelectRowCell,
      headerCell: SelectRowCell.SelectAllHeaderCell,
      width: 3,
      editable: true,
      sortable: false
    })
  },

  async onChangeGridCheckbox(e) {
    e.preventDefault();
    if (this.options.isAssetChangeRequest) {
      this.triggerMethod('grid:checkbox:changed');
    }
  }
});

export default InventoryDashboardGridView;
